import styled from "@emotion/styled";
import { type CSSProperties } from "react";

import {
  ContextMenu,
  FONT_WEIGHT_SEMI_BOLD,
  MenuItem as MuiMenuItem,
  SkiliticsIcon,
  type SkiliticsIconName,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import type { InteractModuleResourceProperties } from "@skilitics/nautilus-endpoint-definition";

import type { Status } from "./shared.ts";

const ContextMenuContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;

  border-radius: 50%;

  button {
    padding: 0;
    &:hover {
      background-color: transparent;
    }
  }
`;

const MenuItem = styled(MuiMenuItem)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;

  font-weight: ${FONT_WEIGHT_SEMI_BOLD};

  &:hover {
    background-color: rgba(0, 0, 0, 0.16);
  }
`;

export type MenuSelection = (key: MenuItemKey) => void;

export type ModuleMenuProps = Pick<CSSProperties, "top"> & {
  module: InteractModuleResourceProperties;
  busy: Status;
  setBusy: (status: Status) => void;
  onMenuOpen: () => void;
  onMenuClose: () => void;
  onMenuSelection: MenuSelection;
};

type MenuItemKey = keyof typeof formatStrings;

export const ModuleMenu = ({ module, busy, onMenuOpen, onMenuClose, onMenuSelection }: ModuleMenuProps) => {
  const formatMessage = useFormatMessage(formatStrings);

  const menuItems = ["lock", "unlock", "duplicate", "delete"].filter((key) => {
    if (module.readonly && key === "lock") return false;
    if (!module.readonly && key === "unlock") return false;
    return true;
  }) as MenuItemKey[];

  return (
    <ContextMenuContainer onDoubleClick={(event) => event.stopPropagation()}>
      <ContextMenu
        disabled={busy !== "idle"}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        data-test="module-actions-button"
      >
        {menuItems.map((name) => (
          <MenuItem key={name} onClick={() => onMenuSelection(name)} data-test="menu-item">
            <SkiliticsIcon icon={actionNameToIcon[name]} />
            {formatMessage(name)}
          </MenuItem>
        ))}
      </ContextMenu>
    </ContextMenuContainer>
  );
};

const actionNameToIcon: Record<MenuItemKey, SkiliticsIconName> = {
  lock: "lock",
  unlock: "unlock",
  duplicate: "context-toolbar-copy",
  delete: "context-toolbar-delete",
};

const formatStrings = {
  lock: {
    en: "Lock",
  },
  unlock: {
    en: "Unlock",
  },
  duplicate: {
    en: "Duplicate",
  },
  delete: {
    en: "Delete",
  },
} satisfies FormatMessages;
